import {
  IServicesWizardConfigs,
  IWizardConfig,
  WizardMode,
  SubmitButtonColor,
  IPrivateClassWizardConfigs,
} from "../../../wizard/types";
import { LANGUAGES } from "../../../language-course/constants";
import { parseManagePrivateClassToBackEndModel } from "../../helpers/parsePrivateClassToBackEndModel";
import {
  parseManagePrivateClassToFrontEndModel,
  parseViewPrivateClassToFrontEndModel,
} from "../../helpers/parsePrivateClassToFrontEndModel";
import usePrivateClassQueries from "../../hooks/usePrivateClassQueries";
import {
  IGetPrivateClassBackEndModel,
  IPrivateClassSubject,
} from "../../types";
import { createConfig } from "../create/config";
import { editConfig } from "../edit/config";
import { viewContentConfig } from "../view/config";
import { useUserId } from "../../../user/hooks/useUserId";

async function getEditInitialData(
  fetchOne: (id: any) => Promise<{ data: IGetPrivateClassBackEndModel }>,
  fetchSubjects: () => Promise<{ data: IPrivateClassSubject[] }>,
  privateClassId: string,
) {
  const [{ data }, { data: subjects }] = await Promise.all([
    fetchOne(privateClassId),
    fetchSubjects(),
  ]);

  const formModel = parseManagePrivateClassToFrontEndModel(data);

  return {
    ...formModel,
    subjectId: subjects.find((s) => s.id === formModel.subjectId) ?? formModel.subjectId,
    teachingLanguage: LANGUAGES.find((l) => l.id === formModel.teachingLanguage) ?? formModel.teachingLanguage,
  };
}

async function getViewData(
  fetchOne: (id: any) => Promise<{ data: IGetPrivateClassBackEndModel }>,
  fetchSubjects: () => Promise<{ data: IPrivateClassSubject[] }>,
  privateClassId: string,
) {
  const [{ data }, { data: subjects }] = await Promise.all([
    fetchOne(privateClassId),
    fetchSubjects(),
  ]);

  const subjectName = subjects.find((s) => s.id === data.subjectId)?.name ?? "";

  return parseViewPrivateClassToFrontEndModel(data, subjectName);
}

export function usePrivateClassConfig(): IPrivateClassWizardConfigs {
  const { create, update, fetchOne, deleteOne, getPrivateClassSubjectList } = usePrivateClassQueries();
  const teacherId = useUserId();

  const getCreateConfig = (): IWizardConfig => ({
    steps: [createConfig],
    submitButtonText: "Save",
    submitButtonColor: SubmitButtonColor.Success,
    mode: WizardMode.Create,
    getInitialData: async () => ({ teacherId }),
    saveCallback: (frontEndModel) => {
      const parsedData = parseManagePrivateClassToBackEndModel(frontEndModel);
      return create(parsedData);
    },
  });

  const getEditConfig = (id: string): IWizardConfig => ({
    steps: [editConfig],
    submitButtonText: "Save",
    submitButtonColor: SubmitButtonColor.Success,
    mode: WizardMode.Create,
    getInitialData: () => getEditInitialData(fetchOne, getPrivateClassSubjectList, id),
    saveCallback: (frontEndModel) => {
      const backEndModel = parseManagePrivateClassToBackEndModel(frontEndModel);
      return update(backEndModel);
    },
  });

  const getViewConfig = (id): IWizardConfig => ({
    steps: [viewContentConfig],
    submitButtonText: null,
    cancelButtonText: "Close",
    mode: WizardMode.View,
    getInitialData: () => getViewData(fetchOne, getPrivateClassSubjectList, id),
  });

  const getDeleteConfig = (id): IWizardConfig => ({
    steps: [
      {
        width: "430px",
        titleConfig: {
          titleText: "Delete private class",
        },
        content: "Are you sure you want to delete this private class?",
      },
    ],
    mode: WizardMode.Delete,
    submitButtonColor: SubmitButtonColor.Danger,
    submitButtonText: "Delete",
    saveCallback: () => deleteOne(id),
  });

  return {
    getCreateConfig,
    getEditConfig,
    getViewConfig,
    getDeleteConfig,
  } as IServicesWizardConfigs;
}
